/**
 * EngineProbe - Runtime capability and permission testing system
 * 
 * Probes each engine to determine if it can actually work at runtime,
 * not just if the APIs are theoretically supported.
 */

import micManager from './MicManager.js';

export class EngineProbe {
    constructor() {
        this.probeResults = new Map();
        this.probeTimeouts = new Map();
        this.permissionListeners = new Map();
        this.isProbing = false;
        this.probeMutex = null; // Mutex for sequential probes
        this.currentGeneration = 0;
        
        // Setup permission change detection
        this.setupPermissionDetection();
        
        console.log('🔍 EngineProbe: Initialized');
    }

    /**
     * Setup permission change detection
     */
    setupPermissionDetection() {
        if (typeof navigator !== 'undefined' && navigator.permissions) {
            // Listen for microphone permission changes
            navigator.permissions.query({ name: 'microphone' }).then(permission => {
                permission.addEventListener('change', () => {
                    console.log('🔍 EngineProbe: Microphone permission changed:', permission.state);
                    this.clearProbeResults();
                });
            }).catch(error => {
                console.warn('🔍 EngineProbe: Could not query microphone permission:', error);
            });
        }
    }

    /**
     * Probe all engines sequentially with mutex
     * @param {Object} options - Engine-specific options
     * @returns {Promise<Object>} Results for all engines
     */
    async probeAllEngines(options = {}) {
        // Acquire mutex
        if (this.probeMutex) {
            console.log('🔍 EngineProbe: Waiting for existing probe to complete...');
            await this.probeMutex;
        }
        
        this.probeMutex = this._probeAllEnginesInternal(options);
        const results = await this.probeMutex;
        this.probeMutex = null;
        
        return results;
    }

    /**
     * Internal method to probe all engines
     * @param {Object} options - Engine-specific options
     * @returns {Promise<Object>} Results for all engines
     */
    async _probeAllEnginesInternal(options = {}) {
        this.currentGeneration++;
        const generation = this.currentGeneration;
        
        console.log(`🔍 EngineProbe: Starting generation ${generation} probe run`);
        
        const engines = ['browser', 'vad', 'streaming'];
        const results = {};
        
        // Set all engines to testing state
        engines.forEach(engine => {
            this._setEngineStatus(engine, 'testing', 'Probing...', generation);
        });
        
        // Probe each engine sequentially
        for (const engine of engines) {
            try {
                const result = await this.probe(engine, options);
                this._setEngineStatus(engine, result.available ? 'available' : 'unavailable', result.reason, generation);
                results[engine] = result;
            } catch (error) {
                this._setEngineStatus(engine, 'unavailable', error.message, generation);
                results[engine] = { available: false, reason: error.message };
            }
        }
        
        console.log(`🔍 EngineProbe: Generation ${generation} probe run complete:`, results);
        return results;
    }

    /**
     * Set engine status in atomic cache
     * @param {string} engine - Engine type
     * @param {string} status - Status: 'available', 'unavailable', 'testing'
     * @param {string} reason - Reason for status
     * @param {number} generation - Generation number
     */
    _setEngineStatus(engine, status, reason, generation) {
        this.probeResults.set(engine, {
            status,
            reason,
            generation,
            timestamp: Date.now()
        });
    }

    /**
     * Get engine status from cache
     * @param {string} engine - Engine type
     * @returns {Object} Status object
     */
    getEngineStatus(engine) {
        return this.probeResults.get(engine) || {
            status: 'unavailable',
            reason: 'Not probed',
            generation: 0,
            timestamp: 0
        };
    }

    /**
     * Probe an engine to determine if it's actually available
     * @param {string} engineType - Type of engine to probe
     * @param {Object} options - Engine-specific options
     * @returns {Promise<Object>} Result object with available and reason
     */
    async probe(engineType, options = {}) {
        // Check if we already have a cached result from current generation
        const cacheKey = this.getCacheKey(engineType, options);
        if (this.probeResults.has(cacheKey)) {
            const result = this.probeResults.get(cacheKey);
            console.log(`🔍 EngineProbe: Using cached result for ${engineType}:`, result.available);
            return result;
        }

        // Check if we're already probing this engine
        if (this.probeTimeouts.has(engineType)) {
            console.log(`🔍 EngineProbe: Already probing ${engineType}, waiting...`);
            return new Promise((resolve) => {
                const checkResult = () => {
                    if (this.probeResults.has(cacheKey)) {
                        resolve(this.probeResults.get(cacheKey).available);
                    } else {
                        setTimeout(checkResult, 100);
                    }
                };
                checkResult();
            });
        }

        console.log(`🔍 EngineProbe: Starting probe for ${engineType}...`);
        this.isProbing = true;

        try {
            const result = await this.performProbe(engineType, options);
            
            // Cache the result
            this.probeResults.set(cacheKey, {
                available: result.available,
                timestamp: Date.now(),
                details: result.details
            });

            console.log(`🔍 EngineProbe: ${engineType} probe result:`, result.available, result.details);
            return result.available;

        } catch (error) {
            console.error(`🔍 EngineProbe: ${engineType} probe failed:`, error);
            
            // Cache the failure
            this.probeResults.set(cacheKey, {
                available: false,
                timestamp: Date.now(),
                details: { error: error.message }
            });

            return false;
        } finally {
            this.isProbing = false;
            this.probeTimeouts.delete(engineType);
        }
    }

    /**
     * Perform the actual probe for a specific engine
     * @param {string} engineType - Type of engine to probe
     * @param {Object} options - Engine-specific options
     * @returns {Promise<Object>} Probe result with availability and details
     */
    async performProbe(engineType, options = {}) {
        const startTime = Date.now();
        const details = {};

        try {
            switch (engineType) {
                case 'browser':
                    return await this.probeBrowserSTT(options, details);
                case 'vad':
                    return await this.probeVADEngine(options, details);
                case 'streaming':
                    return await this.probeStreamingSTT(options, details);
                case 'mock':
                    return await this.probeMockSTT(options, details);
                default:
                    throw new Error(`Unknown engine type: ${engineType}`);
            }
        } catch (error) {
            return {
                available: false,
                details: {
                    error: error.message,
                    duration: Date.now() - startTime
                }
            };
        }
    }

    /**
     * Probe BrowserSTT engine
     */
    async probeBrowserSTT(options, details) {
        const startTime = Date.now();
        
        try {
            // Check basic API support
            if (!('SpeechRecognition' in window) && !('webkitSpeechRecognition' in window)) {
                details.error = 'SpeechRecognition API not supported';
                return { available: false, details };
            }

            // Test microphone access
            details.micTest = await this.testMicrophoneAccess();
            if (!details.micTest.success) {
                details.error = 'Microphone access failed: ' + details.micTest.error;
                return { available: false, details };
            }

            // Test speech recognition creation
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognition = new SpeechRecognition();
            
            details.recognitionCreated = true;
            details.recognitionConfig = {
                continuous: false,
                interimResults: true,
                language: options.language || 'en-US'
            };

            // Test recognition start/stop cycle
            const recognitionTest = await this.testRecognitionCycle(recognition);
            details.recognitionTest = recognitionTest;
            
            if (!recognitionTest.success) {
                details.error = 'Recognition cycle failed: ' + recognitionTest.error;
                return { available: false, details };
            }

            return {
                available: true,
                details: {
                    ...details,
                    duration: Date.now() - startTime
                }
            };

        } catch (error) {
            return {
                available: false,
                details: {
                    ...details,
                    error: error.message,
                    duration: Date.now() - startTime
                }
            };
        }
    }

    /**
     * Probe VAD engine
     */
    async probeVADEngine(options, details) {
        const startTime = Date.now();
        
        try {
            // Check basic API support
            if (!('AudioContext' in window) && !('webkitAudioContext' in window)) {
                details.error = 'AudioContext not supported';
                return { available: false, details };
            }

            if (!('AudioWorkletNode' in window)) {
                details.error = 'AudioWorkletNode not supported';
                return { available: false, details };
            }

            // Test microphone access
            details.micTest = await this.testMicrophoneAccess();
            if (!details.micTest.success) {
                details.error = 'Microphone access failed: ' + details.micTest.error;
                return { available: false, details };
            }

            // Test AudioContext creation
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioContextClass();
            details.audioContextCreated = true;
            details.audioContextState = audioContext.state;

            // Test AudioWorklet loading
            const workletTest = await this.testAudioWorklet(audioContext);
            details.workletTest = workletTest;
            
            if (!workletTest.success) {
                details.error = 'AudioWorklet test failed: ' + workletTest.error;
                audioContext.close();
                return { available: false, details };
            }

            // Test MediaStreamSource creation
            const stream = await micManager.acquire();
            const source = audioContext.createMediaStreamSource(stream);
            details.mediaStreamSourceCreated = true;

            // Cleanup
            source.disconnect();
            micManager.release();
            audioContext.close();

            return {
                available: true,
                details: {
                    ...details,
                    duration: Date.now() - startTime
                }
            };

        } catch (error) {
            return {
                available: false,
                details: {
                    ...details,
                    error: error.message,
                    duration: Date.now() - startTime
                }
            };
        }
    }

    /**
     * Probe StreamingSTT engine
     */
    async probeStreamingSTT(options, details) {
        const startTime = Date.now();
        
        try {
            // Check WebSocket support
            if (!('WebSocket' in window)) {
                details.error = 'WebSocket not supported';
                return { available: false, details };
            }

            // Test microphone access
            details.micTest = await this.testMicrophoneAccess();
            if (!details.micTest.success) {
                details.error = 'Microphone access failed: ' + details.micTest.error;
                return { available: false, details };
            }

            // Test WebSocket connection
            const wsTest = await this.testWebSocketConnection(options);
            details.wsTest = wsTest;
            
            if (!wsTest.success) {
                details.error = 'WebSocket connection failed: ' + wsTest.error;
                return { available: false, details };
            }

            return {
                available: true,
                details: {
                    ...details,
                    duration: Date.now() - startTime
                }
            };

        } catch (error) {
            return {
                available: false,
                details: {
                    ...details,
                    error: error.message,
                    duration: Date.now() - startTime
                }
            };
        }
    }

    /**
     * Probe MockSTT engine
     */
    async probeMockSTT(options, details) {
        const startTime = Date.now();
        
        try {
            // Mock engine is always available
            return {
                available: true,
                details: {
                    mockEngine: true,
                    duration: Date.now() - startTime
                }
            };

        } catch (error) {
            return {
                available: false,
                details: {
                    error: error.message,
                    duration: Date.now() - startTime
                }
            };
        }
    }

    /**
     * Test microphone access
     */
    async testMicrophoneAccess() {
        try {
            const stream = await micManager.acquire();
            const audioTracks = stream.getAudioTracks();
            const liveTracks = audioTracks.filter(track => track.readyState === 'live');
            
            micManager.release();
            
            return {
                success: liveTracks.length > 0,
                trackCount: audioTracks.length,
                liveTrackCount: liveTracks.length
            };
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Test speech recognition cycle
     */
    async testRecognitionCycle(recognition) {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                resolve({ success: false, error: 'Recognition test timeout' });
            }, 2000);

            let started = false;
            let ended = false;

            recognition.onstart = () => {
                started = true;
            };

            recognition.onend = () => {
                ended = true;
                clearTimeout(timeout);
                resolve({ success: started && ended, started, ended });
            };

            recognition.onerror = (event) => {
                clearTimeout(timeout);
                resolve({ success: false, error: event.error, started, ended });
            };

            try {
                recognition.start();
                // Stop immediately to test the cycle
                setTimeout(() => {
                    if (started && !ended) {
                        recognition.stop();
                    }
                }, 100);
            } catch (error) {
                clearTimeout(timeout);
                resolve({ success: false, error: error.message, started, ended });
            }
        });
    }

    /**
     * Test AudioWorklet loading
     */
    async testAudioWorklet(audioContext) {
        try {
            await audioContext.audioWorklet.addModule('/vad-processor.js');
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    /**
     * Test WebSocket connection
     */
    async testWebSocketConnection(options) {
        return new Promise((resolve) => {
            const serverUrl = options.serverUrl || process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            const wsUrl = serverUrl.replace('http', 'ws') + '/streaming-stt/ws/test';
            
            const timeout = setTimeout(() => {
                resolve({ success: false, error: 'WebSocket connection timeout' });
            }, 3000);

            try {
                const ws = new WebSocket(wsUrl);
                
                ws.onopen = () => {
                    clearTimeout(timeout);
                    ws.close();
                    resolve({ success: true, url: wsUrl });
                };
                
                ws.onerror = (error) => {
                    clearTimeout(timeout);
                    resolve({ success: false, error: 'WebSocket connection failed', url: wsUrl });
                };
                
                ws.onclose = (event) => {
                    if (event.code !== 1000) {
                        clearTimeout(timeout);
                        resolve({ success: false, error: `WebSocket closed with code ${event.code}`, url: wsUrl });
                    }
                };
                
            } catch (error) {
                clearTimeout(timeout);
                resolve({ success: false, error: error.message, url: wsUrl });
            }
        });
    }

    /**
     * Get cache key for probe results
     */
    getCacheKey(engineType, options) {
        return `${engineType}_${JSON.stringify(options)}`;
    }

    /**
     * Clear all probe results
     */
    clearProbeResults() {
        console.log('🔍 EngineProbe: Clearing probe results');
        this.probeResults.clear();
    }

    /**
     * Get probe results for debugging
     */
    getProbeResults() {
        const results = {};
        for (const [key, value] of this.probeResults.entries()) {
            results[key] = value;
        }
        return results;
    }

    /**
     * Check if we're currently probing
     */
    isCurrentlyProbing() {
        return this.isProbing;
    }

    /**
     * Get probe statistics
     */
    getProbeStats() {
        const results = Array.from(this.probeResults.values());
        const available = results.filter(r => r.available).length;
        const total = results.length;
        
        return {
            total,
            available,
            unavailable: total - available,
            isProbing: this.isProbing
        };
    }
}

// Create and export singleton instance
const engineProbe = new EngineProbe();

// Export named helpers for easier usage (named functions)
export function probe(...args) {
    return engineProbe.probe(...args);
}

export function getProbeStats() {
    return engineProbe.getProbeStats();
}

export async function probeAllEngines(...args) {
    return engineProbe.probeAllEngines(...args);
}

export function getEngineStatus(engine) {
    return engineProbe.getEngineStatus(engine);
}

export default engineProbe;
